import casesService from "api/casesService";
import CasesContext from "contexts/CasesContext";
import { IEntity } from "dto/IEntity";
import { IParcelTypes } from "dto/IParcelTypes";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  convertEParcelTypeToIParcelType,
  convertParcelTypeToKind,
  getTypeOfParcel,
  translateToPolish,
} from "utils/getTypeOfParcel";
import { validateEmail } from "utils/isEmail";
import styles from "./styles.module.scss";
import { Props } from "./typings";
import Avatar from "components/Avatar";
import { useOnClickOutside } from "usehooks-ts";
import Button from "components/Button";
import { validateEDeliveryAddress } from "utils/iseDeliveryAddress";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import workspaceService from "api/workspaceService";
import { IWorkspace, IWorkspaceMember } from "dto/IWorkspace";
import InputWithChips from "components/InputWithChips";
import classNames from "classnames";
import { Tooltip } from "@mui/material";
import ListControl from "components/ListControl";
import { ListItem } from "components/ListControl/typings";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import Spinner from "components/Spinner";
import RemoveIcon from "assets/icons/bin.svg?react";
import ExpandIcon from "assets/icons/expand.svg?react";
import CollapseIcon from "assets/icons/collapse.svg?react";
import EmailIcon from "assets/icons/aemail.svg?react";
import LetterIcon from "assets/icons/new_parcel.svg?react";
import NotaIcon from "assets/icons/nota_icon.svg?react";
import AdeIcon from "assets/icons/ade.svg?react";
import CourtIcon from "assets/icons/court.svg?react";
import { EEntityType } from "dto/EEntityType";
import { EParcelType } from "dto/EParcelType";
import { randomString } from "utils/randomString";
import parcelService from "api/parcelService";
import HollowButton from "components/HollowButton";
import CopyIcon from "assets/icons/copy.svg?react";
import { Option } from "components/OptionsCard/typings";
import DotsIcon from "assets/icons/three-dots.svg?react";
import Popper from "components/Popper";
import { default as Dropdown2 } from "components/OptionsCard/Dropdown";
import LabelIcon from "assets/icons/label-new.svg?react";
import InputInline from "components/InputInline";
import CaseParticipantEditorModal from "components/CaseParticipantEditorModal";
import InfoIcon from "assets/icons/info_icon.svg?react";
import DropdownMui from "components/DropdownMui";
import { logger } from "utils/logger";
import { IDropdownObject } from "components/Dropdown/typings";
import { getDefaultParcelType } from "utils/getDefaultParcelType";
import { useTranslation } from "react-i18next";
import SearchBaeModal from "components/SearchBaeModal";
import { IGovAddressSearchResult } from "api/eDeliveryGovService";

interface IEntityAutocompleteItem {
  Id: string;
  Group: string;
  Label: string;
  ParcelType: IParcelTypes;
  Entity: IEntity;
  IsSearchAdeButton?: boolean;
  InternalId: string;
  IsBusy?: boolean;
  NotFound?: boolean;
}

export const fillIcon = (entity: IEntity) => {
  switch (entity.parcelTypeSelected) {
    case IParcelTypes.EDelivery:
    case IParcelTypes.EncryptedEDelivery:
    case IParcelTypes.ExternalEDelivery:
      if (!entity.EDeliveryAddress) {
        return;
      }

      entity.Icon = <AdeIcon></AdeIcon>;

      break;
    case IParcelTypes.Email:
      entity.Icon = <EmailIcon></EmailIcon>;
      break;
    case IParcelTypes.NotaEncrypted:
      entity.Icon = <NotaIcon></NotaIcon>;
      break;
    case IParcelTypes.CourtPortalWriting:
      entity.Icon = <CourtIcon></CourtIcon>;
      break;
    default:
      entity.Icon = <LetterIcon></LetterIcon>;
  }
};

const Address = ({
  cas,
  types,
  onSelect,
  selectedType,
  setSelectedType,
}: {
  cas: any;
  types: IParcelTypes[];
  onSelect: (cas: IEntity, type: IParcelTypes) => void;
  selectedType: IParcelTypes | null;
  setSelectedType: (type: IParcelTypes | null) => void;
}) => {
  const [showed, setShowed] = useState(false);

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    if (showed) {
      setShowed(false);
    }
  });

  const handleChange = (v?: IDropdownObject) => {
    logger.log("Changed", v);
    setSelectedType(v.value);
  };

  return (
    <div className={styles.adressWrapper}>
      <DropdownMui
        allowNull={true}
        list={types.map((x) => ({ value: x, name: translateToPolish(x) }))}
        onChange={handleChange}
        defaultValue={selectedType}
        variant="standard"
        hideBorder={true}
      ></DropdownMui>
    </div>
  );
};

const AddressListRow = ({
  participant,
  list,
  setList,
  onSelect,
  afterEdit,
}: {
  participant: ICaseParticipant;
  list: { entity: IEntity; type: IParcelTypes | null }[];
  setList: (list: { entity: IEntity; type: IParcelTypes | null }[]) => void;
  onSelect: (cas: IEntity, type: IParcelTypes) => void;
  afterEdit: (participant: ICaseParticipant) => void;
}) => {
  const { t } = useTranslation();
  const types: IParcelTypes[] = getTypeOfParcel(participant.ParcelTypes ?? 0);
  const refWrapper = useRef(null);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [editInline, setEditInline] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [options, setOptions] = useState<Option[]>([]);

  const [name] = useState<string>(participant.Label);

  const commitName = (val?: string) => {
    console.log("Setting name", val ?? name);
    participant.Name = val ?? name;
    participant.Label = val ?? name;
    afterEdit(participant);
    setEditInline(false);
  };

  const handleUpdateParticipant = (p: ICaseParticipant) => {
    afterEdit(p);
  };

  const handleEditInline = () => {
    setEditInline(true);
  };

  useEffect(() => {
    const opt = [
      {
        name: t("entities.editName"),
        Icon: LabelIcon,
        callback: () => {
          handleEditInline();
        },
        additional: true,
        isEnabled: true,
        canMultiple: true,
      },
      {
        name: t("entities.editData"),
        Icon: LabelIcon,
        callback: () => {
          setShowInfo(true);
        },
        additional: true,
        isEnabled: true,
        canMultiple: true,
      },
    ];

    setOptions(opt);
  }, [participant]);

  if (editInline) {
    return (
      <div className={styles.box}>
        <div className={styles.boxs}>
          <div
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) return;
              console.log("Box click called");
              const type = list.find((a) => a.entity === participant);

              //@ts-ignore
              onSelect(participant, type.type);
              e.stopPropagation();
            }}
            className={styles.data}
          >
            <div className={styles.avatar}>
              <Avatar
                className={styles.avatar}
                circleClassName={styles.avatar}
                name={participant.Label ?? participant.Email ?? ""}
                size="small"
              />
            </div>
            <div className={styles.dataText}>
              <InputInline
                value={participant.Label}
                onCancel={() => setEditInline(false)}
                onChange={commitName}
              ></InputInline>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.box}>
      <div className={styles.boxs}>
        <div
          onClick={() => {
            // if (e.ctrlKey || e.metaKey) return;
            // console.log("Box click called");
            // const type = list.find((a) => a.entity === participant);
            // //@ts-ignore
            // onSelect(participant, type.type);
            // e.stopPropagation();
          }}
          className={styles.data}
        >
          <div className={styles.avatar}>
            <Avatar
              className={styles.avatar}
              circleClassName={styles.avatar}
              name={participant.Label ?? participant.Email ?? ""}
              size="small"
            />
          </div>
          <div className={styles.dataText}>
            <div className={styles.text}>
              {participant.Label ?? participant.Email}
            </div>
            <div className={styles.icon}>
              <Tooltip title={t("entities.entityData")}>
                <div>
                  <InfoIcon onClick={() => setShowInfo(!showInfo)}></InfoIcon>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.addressList}>
          <Address
            selectedType={
              list?.find((a) => a.entity.Id === participant.Id)?.type ?? null
            }
            setSelectedType={(typwe: IParcelTypes | null) => {
              const newList = [...list];

              const element = newList.find(
                (a) => a.entity.Id === participant.Id,
              );

              if (element) {
                element.type = typwe;
                setList(newList);
                return;
              }

              //@ts-ignore
              newList.push({ entity: participant, type: typwe });
              setList(newList);
            }}
            onSelect={onSelect}
            cas={participant}
            types={types}
          />
        </div>
        <div className={styles.more} ref={refWrapper}>
          <Tooltip title={t("common.more")}>
            <div>
              <DotsIcon
                onClick={() => {
                  setShowDropdown(!showDropdown);
                }}
                className={classNames(
                  styles.icon,
                  participant.HasConflict ? styles.conflict : null,
                )}
              />
            </div>
          </Tooltip>
        </div>
        <Popper
          anchorEl={refWrapper?.current}
          open={showDropdown}
          setOpen={setShowDropdown}
        >
          <Dropdown2
            toggle={() => setShowDropdown(false)}
            options={options}
            refWrapper={refWrapper}
            rightSide={false}
            scrollWrapper={refWrapper}
            showOptions={true}
            selectedElements={[]}
          />
        </Popper>

        {showInfo && (
          <Popper
            anchorEl={refWrapper?.current}
            open={showInfo}
            setOpen={setShowInfo}
          >
            <CaseParticipantEditorModal
              toggle={() => setShowInfo(false)}
              source={participant}
              update={handleUpdateParticipant}
            ></CaseParticipantEditorModal>
          </Popper>
        )}
      </div>
    </div>
  );
};

const AddressList = ({
  caseId,
  toggle,
  onSelect,
  addAll,
  selectedItems,
  setSelectedItems,
}: {
  caseId: string;
  toggle: () => void;
  addAll: (
    entities: IEntity[],
    list: { entity: IEntity; type: IParcelTypes | null }[],
  ) => void;
  onSelect: (cas: IEntity, type: IParcelTypes) => void;
  selectedItems: ListItem[];
  setSelectedItems: (items: ListItem[]) => void;
}) => {
  const { t } = useTranslation();
  const { data: caseData, isLoading } = casesService.useGetCase(caseId ?? "");

  const c = useMemo(() => caseData ?? null, [caseData]);

  const [list, setList] = useState<
    { entity: IEntity; type: IParcelTypes | null }[]
  >([]);

  useEffect(() => {
    const m = c?.Participants.map((cas) => {
      const types: IParcelTypes[] = getTypeOfParcel(cas.ParcelTypes ?? 0);
      const defaultParcelType = cas.DefaultParcelType;

      if (!defaultParcelType) {
        return null;
      }

      const typ = getTypeOfParcel(defaultParcelType);

      const m = types.find((t) => t === typ[0]) ?? types?.[0];

      // żeby zachowywało się jak Entity
      if (cas.ParticipantId) {
        cas.Id = cas.ParticipantId;
      } else if (cas.UserId) {
        cas.Id = cas.UserId;
      }

      return { entity: cas, type: m };
    }).filter((c) => c !== null);

    //@ts-ignore
    setList(m);
  }, [c]);

  const updateParticipantAfterEdit = (
    item: ICaseParticipant,
    newValue?: ICaseParticipant,
  ) => {
    const copy = [...list];
    const part = copy.find((x) => x.entity.Id === item.Id);

    if (part) {
      const index = copy.indexOf(part);
      copy[index].entity = newValue as IEntity;
      setList(copy);
    }
  };

  const ref = useRef(null);

  useOnClickOutside(ref, () => {
    // if (e.target instanceof HTMLElement && !isRootElementAPopper(e.target)) {
    //   toggle();
    // }
  });

  return (
    <div ref={ref} className={`${styles.list} ${styles.list2} ${styles.b}`}>
      <div className={styles.wrapper}>
        <div className={styles.listHeader}>
          <div className={styles.c1}>{t("navigation.entities")}</div>
          <div className={styles.c2}>{t("entities.deliveryForm")}</div>
        </div>
        <div className={styles.entityList}>
          {isLoading && <Spinner></Spinner>}
          {!isLoading && (
            <ListControl
              items={c?.Participants}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              getItemId={(i) => i.Id}
              renderItem={(i) => {
                const p = i as ICaseParticipant;

                return (
                  <AddressListRow
                    participant={p}
                    list={list}
                    onSelect={onSelect}
                    setList={setList}
                    afterEdit={(n) => updateParticipantAfterEdit(p, n)}
                  ></AddressListRow>
                );
              }}
            ></ListControl>
          )}
        </div>
      </div>
      <div className={styles.buttons}>
        <HollowButton
          onClick={() => {
            //@ts-ignore
            addAll(c!.Participants!, list);
            toggle();
          }}
          text={t("entities.addAll")}
        />

        <Button
          disabled={selectedItems.length === 0}
          onClick={() => {
            //@ts-ignore
            addAll(selectedItems, list);
            toggle();
          }}
          text={t("entities.addSelected")}
        />
      </div>
    </div>
  );
};

const NewSearchEntitiesWithCase: FC<Props> = ({
  id,
  placeholder = "",
  c,
  onChoose,
  className,
  showAlways = false,
  initialEntities,
  entities: listEntities,
  defaultValue,
  focus = false,
  isFullscreen,
  showWorkspaceMembers,
  onChooseMembers,
  setShowDw,
  onBlur,
}) => {
  const { t } = useTranslation();
  const [showLimitedReceiversList, setShowLimitedReceiversList] =
    useState(false);
  const [limitTags, setLimitTags] = useState<boolean>(() => {
    const savedState = localStorage.getItem(`${id}_limit`);
    return savedState ? JSON.parse(savedState) : false;
  });
  const [rightPadding, setRightPadding] = useState(0);
  const ref = useRef(null);
  const limitedReceiversListRef = useRef(null);
  const [selected, setSelected] = useState<IEntity[]>([]);
  const [selectedItems] = useState<ListItem[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<IWorkspaceMember[]>(
    [],
  );
  const [active, setActive] = useState(false);
  const [showBaeSearch, setShowBaeSearch] = useState(false);
  const [value, setValue] = useState("");
  const [debouncedText] = useDebounce(value, 500);
  const [searchList, setSearchList] = useState<IEntity[]>([]);

  const [stopSearchById, setStopSearchById] = useState(false);
  const [searchedCaseId, setSearchedCaseId] = useState<null | string>(null);

  const { data: entitiesData, isLoading: isLoadingAddressBook } =
    casesService.useGetAddressBook(
      debouncedText,
      null,
      c?.Id,
      false,
      debouncedText !== null && debouncedText.length > 0,
    );
  const { mutateAsync: verifyReceiver } = parcelService.useVerifyReceiver();
  const { mutate: searchAdeForEntit } = parcelService.useSearchAdeForEntity();

  const { data: caseData } = casesService.useGetCase(searchedCaseId ?? "");

  const { cases } = useContext(CasesContext);

  const [workspaceId] = useState(getWorkspaceId() ?? "");

  const { data: workspaceData } = workspaceService.useGetWorkspace(workspaceId);

  const workspace: IWorkspace = useMemo(
    () => (workspaceData?.data as IWorkspace) ?? [],
    [workspaceData],
  );

  const workspaceMembers = useMemo(() => workspace?.Members ?? [], [workspace]);

  const [searchedMembers, setSearchedMembers] = useState<IWorkspaceMember[]>(
    [],
  );

  const [entitiesOptions, setEntitiesOptions] = useState<
    IEntityAutocompleteItem[]
  >([]);

  const { data: fullCaseData } = casesService.useGetCase(
    c?.Id,
    null,
    c?.Id !== null,
  );

  const fullCase = useMemo(() => fullCaseData ?? null, [fullCaseData]);

  useEffect(() => {
    console.log("Initial entities changed");
    const newEntities = initialEntities.filter(
      (x) => !selected.find((e) => e.Id === x.Id),
    );

    setSelected([...selected, ...newEntities]);
  }, [initialEntities]);

  useEffect(() => {
    localStorage.setItem(`${id}_limit`, JSON.stringify(limitTags));
  }, [limitTags]);

  useEffect(() => {
    if (!focus) return;
    setActive(true);
  }, [focus]);

  useOnClickOutside(ref, () => {
    setSelectedEntity(null);
  });

  useOnClickOutside(limitedReceiversListRef, () => {
    setShowLimitedReceiversList(false);
  });

  const entities: IEntity[] = useMemo(
    () => entitiesData?.data ?? [],
    [entitiesData],
  );

  const getEntityAddressForParcelType = (
    e: IEntity,
    parcelType: IParcelTypes,
  ) => {
    switch (parcelType) {
      case IParcelTypes.EDelivery:
      case IParcelTypes.EncryptedEDelivery:
      case IParcelTypes.ExternalEDelivery:
        return e.EDeliveryAddress;
      case IParcelTypes.Email:
        return e.Email;
      case IParcelTypes.TraditionalLetter:
      case IParcelTypes.Puh:
        return e.Address;
      case IParcelTypes.NotaEncrypted:
        return t("entities.encryptedNotaPackage");
      case IParcelTypes.CourtPortalWriting:
        return t("entities.courtPortalPackage");
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!entitiesData) setEntitiesOptions([]);

    const data = entitiesData?.data.flatMap((e) => {
      const types = getTypeOfParcel(e.ParcelTypes ?? 0);

      let toRet = types.map((t) => ({
        Id: e.ParticipantId ?? e.UserId,
        Group: "GGG" + (e.Label ?? e.Email ?? e.EDeliveryAddress),
        Label: getEntityAddressForParcelType(e, t),
        ParcelType: t,
        Entity: e,
        IsSearchAdeButton: false,
        InternalId: randomString(),
      }));

      if (e.IsGlobal && (e.ParcelTypes & EParcelType.EDelivery) === 0) {
        toRet = [
          ...toRet,
          {
            Id: e.ParticipantId ?? e.UserId,
            Group: "GGG" + (e.Label ?? e.Email ?? e.EDeliveryAddress),
            Label: t("entities.searchAddress"),
            ParcelType: IParcelTypes.EDelivery,
            Entity: e,
            IsSearchAdeButton: true,
            InternalId: randomString(),
          },
        ];
      }

      return toRet;
    });

    let caseEntities = [];

    if (fullCase && fullCase.Participants) {
      caseEntities = fullCase.Participants.map((e) => ({
        Id: e.ParticipantId ?? e.UserId,
        CustomGroup: t("entities.caseAddressList"),
        Group: "GGG" + "Lista adresowa",
        Label: e.Label,
        ParcelType: convertEParcelTypeToIParcelType(
          e.DefaultParcelType ?? getDefaultParcelType(e.ParcelTypes),
        ),
        Additional:
          e.DefaultParcelType || getDefaultParcelType(e.ParcelTypes)
            ? getEntityAddressForParcelType(
                e,
                convertEParcelTypeToIParcelType(
                  e.DefaultParcelType ?? getDefaultParcelType(e.ParcelTypes),
                ),
              )
            : null,
        Entity: e,
        InternalId: randomString(),
      }));
    }

    console.log(caseEntities);
    setEntitiesOptions([...caseEntities, ...(data ?? [])]);
  }, [entitiesData, fullCase]);

  const searchAdeForEntity = (entity: IEntityAutocompleteItem) => {
    const newList = [...entitiesOptions];
    const toUpdate = newList.find((x) => x.InternalId === entity.InternalId);

    if (toUpdate != null) {
      toUpdate.IsBusy = true;
    }

    searchAdeForEntit(entity.Entity.Id, {
      onSuccess(data) {
        toUpdate.IsBusy = false;

        if (data.data) {
          toUpdate.Entity.EDeliveryAddress = data.data.EDeliveryAddress;
          toUpdate.Label = data.data.EDeliveryAddress;
          toUpdate.IsSearchAdeButton = false;
          toUpdate.ParcelType = IParcelTypes.EDelivery;
        }

        setEntitiesOptions(newList);
      },
      onError() {
        toUpdate.IsBusy = false;
        toUpdate.NotFound = true;
        setEntitiesOptions(newList);
      },
    });

    setEntitiesOptions(newList);
  };

  const [open, setOpen] = useState(false);
  //const [options, setOptions] = useState<readonly IEntity[]>([]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    //setOptions([]);
  };

  const isCase = window.location.href.search("cases") !== -1;

  useEffect(() => {
    if (caseData?.Participants) {
      const e = caseData.Participants.map((p) => {
        const types = getTypeOfParcel(p.ParcelTypes ?? 0);

        //@ts-ignore
        p.parcelTypeSelected = types[0] ?? null;

        return p;
      });

      //@ts-ignore
      setSelected(e);
      //@ts-ignore
      onChoose?.(e);
    }
  }, [caseData]);

  useEffect(() => {
    if (searchedCaseId && !stopSearchById) {
      if (c) {
        setSearchedCaseId(null);

        setStopSearchById(true);
      }
    }

    if (isCase && !stopSearchById) {
      // setSearchedCaseId(caseId!);
    }
  }, [isCase, cases]);

  useEffect(() => {
    setSearchList(entities);
  }, [entities]);

  useEffect(() => {
    if (!value.length) {
      setSearchList(entities);

      if (showWorkspaceMembers) {
        setSearchedMembers(workspaceMembers);
      }
      return;
    }

    // filtracja
    if (showWorkspaceMembers) {
      setSearchedMembers(
        workspaceMembers.filter(
          (m) =>
            m.Label.toLocaleLowerCase().search(value.toLocaleLowerCase()) !==
            -1,
        ),
      );
    }
  }, [value, workspaceMembers, showWorkspaceMembers]);

  const addAll = (
    entities: IEntity[],
    list: { entity: IEntity; type: IParcelTypes | null }[],
  ) => {
    console.log("addAll");
    const readyEntities = entities.map((e) => {
      const parcelType = list.find((p) => p.entity.Id === e.Id)?.type;

      if (!parcelType) {
        const types: IParcelTypes[] = getTypeOfParcel(e.ParcelTypes ?? 0);

        e.parcelTypeSelected = types[0];
      }

      e.parcelTypeSelected = parcelType!;

      switch (parcelType) {
        case IParcelTypes.EDelivery:
        case IParcelTypes.EncryptedEDelivery:
        case IParcelTypes.ExternalEDelivery:
          e.Icon = <AdeIcon></AdeIcon>;
          break;
        case IParcelTypes.Email:
          e.Icon = <EmailIcon></EmailIcon>;
          break;
        case IParcelTypes.NotaEncrypted:
          e.Icon = <NotaIcon></NotaIcon>;
          break;
        case IParcelTypes.CourtPortalWriting:
          e.Icon = <CourtIcon></CourtIcon>;
          break;
        default:
          e.Icon = <LetterIcon></LetterIcon>;
      }

      return e;
    });

    setSelected([...selected, ...readyEntities]);
    onChoose?.(readyEntities);
  };

  const updateEntity = (initialData: IEntity, newData: IEntity) => {
    setSelected((old) => {
      const newList = [...old];
      console.log("UPDATE ENTITY", newList, initialData);
      const toUpdate = newList.filter(
        (x) => x.internalId === initialData.internalId,
      );

      if (toUpdate.length > 0) {
        console.log("To update found");
        const t = toUpdate[0];

        t.isBusy = false;
        t.isValid = true;
        t.Label = newData.Label;
        t.IsPublic = newData.IsPublic;
        t.NIP = newData.NIP;
        t.KRS = newData.KRS;
        t.REGON = newData.REGON;

        if (
          t.parcelTypeSelected === IParcelTypes.EDelivery ||
          t.parcelTypeSelected === IParcelTypes.EncryptedEDelivery
        ) {
          if ((newData.ParcelTypes & EParcelType.EncryptedEDelivery) > 0) {
            newData.parcelTypeSelected = IParcelTypes.EncryptedEDelivery;
          } else {
            newData.parcelTypeSelected = IParcelTypes.EDelivery;
          }

          t.Icon = <AdeIcon></AdeIcon>;
        }
      }
      return newList;
    });
  };

  const validateEntity = (entity: IEntity) => {
    if (!entity.internalId) {
      entity.internalId = randomString();
    }

    verifyReceiver({
      Id: entity.Id,
      Type: entity.Type,
      Parcel: convertParcelTypeToKind(entity.parcelTypeSelected!),
      Email: entity.Email,
      EDeliveryAddress: entity.EDeliveryAddress,
    })
      .then((value) => {
        updateEntity(entity, value.data);
      })
      .catch(() => {
        setSelected((old) => {
          const newList = [...old];

          const toUpdate = newList.filter(
            (x) => x.internalId === entity.internalId,
          );

          if (toUpdate.length > 0) {
            const t = toUpdate[0];
            t.isBusy = false;
            t.isValid = false;
          }

          return newList;
        });
      });

    // searchEDeliveryAddress(ade, {
    //   onSuccess(data: any, variables, context) {
    //     if (data && data.data && data.data.length > 0) {
    //       handleClick(

    //         IParcelTypes.EDelivery
    //       );
    //     }
    //   },
    // });
  };

  const handleClick = (cas: IEntity, type: IParcelTypes) => {
    console.log("Handle click", cas, type);
    cas.parcelTypeSelected = type;

    fillIcon(cas);

    const newList = [...selected, cas];
    setSelected(newList);
    // setValue(`${cas.Client ? `${cas.Client} |` : ''} ${cas.Title}`);
    setActive(false);

    onChoose?.(newList);
    setValue("");

    if (type === IParcelTypes.EDelivery) {
      validateEntity(cas);
    }
  };

  const handleOnChange = (items: any[]) => {
    const newList = [...selected];

    console.log(items);
    items.forEach((i) => {
      const e = i.Entity;
      if (e) {
        let add = true;
        e.parcelTypeSelected = i.ParcelType;

        switch (i.ParcelType) {
          case IParcelTypes.EDelivery:
          case IParcelTypes.EncryptedEDelivery:
            if (!e.EDeliveryAddress) {
              console.log(e);
              add = false;
            } else {
              e.Icon = <AdeIcon></AdeIcon>;
            }

            break;
          case IParcelTypes.Email:
            e.Icon = <EmailIcon></EmailIcon>;
            break;
          case IParcelTypes.NotaEncrypted:
            e.Icon = <NotaIcon></NotaIcon>;
            break;
          case IParcelTypes.CourtPortalWriting:
            e.Icon = <CourtIcon></CourtIcon>;
            break;
          default:
            e.Icon = <LetterIcon></LetterIcon>;
        }
        if (add) {
          if (i.ParcelType === IParcelTypes.EDelivery) {
            e.isBusy = true;
            validateEntity(e);
          }

          newList.push(e);
        }
      }
    });

    setSelected(newList);
    onChoose?.(newList);
    setValue("");
  };

  const handleMemberClick = (cas: IWorkspaceMember) => {
    const newList = [...selectedMembers, cas];
    setSelectedMembers(newList);
    // setValue(`${cas.Client ? `${cas.Client} |` : ''} ${cas.Title}`);
    setActive(false);

    onChooseMembers?.(newList);
    setValue("");
  };

  const handleRemove = (cas: IEntity) => {
    const newList = selected.filter((c) => c.Id !== cas.Id);
    setSelected(newList);
    onChoose?.(newList);
  };

  const handleMemberRemove = (cas: string) => {
    // console.log(selected);
    // console.log(cas);
    const newList = selected.filter((c) => c.Id != cas);
    // setSelectedMembers(newList);
    onChooseMembers?.(newList);
    onChoose?.(newList);
    setSelected(newList);
  };

  useEffect(() => {
    console.log("Selected changed", selected);
    onChoose?.(selected);
  }, [selected]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      //@ts-ignore
      if (ref.current && !ref.current.contains(event.target as Element)) {
        setActive(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    if (!defaultValue && !defaultValue?.length) return;
    const payload: IEntity = {
      Id: randomString(),
      Address: "",
      BuildingNumber: "",
      City: "",
      Claims: [],
      Description: "",
      EDeliveryAddress: defaultValue,
      Email: defaultValue,
      FirstName: "",
      FlatNumber: "",
      IsGlobal: false,
      KRS: "",
      Label: defaultValue,
      Type: EEntityType.Email,
      ParcelTypes: EParcelType.Email,
      parcelTypeSelected: IParcelTypes.Email,
    };
    setSelected([payload]);
  }, []);

  const checkCanAdd = (disableCheckCharAt: boolean = false) => {
    if (value.indexOf(",") !== -1) {
      const emails = value.split(",");

      const list: IEntity[] = [];

      emails.forEach((e) => {
        const em = validateEmail(e);
        if (em) {
          list.push({
            Id: randomString(),
            Address: "",
            BuildingNumber: "",
            City: "",
            Claims: [],
            Description: "",
            EDeliveryAddress: null,
            Email: em.address,
            FirstName: "",
            FlatNumber: "",
            IsGlobal: false,
            KRS: "",
            Label: em.label ?? em.address,
            Type: EEntityType.Email,
            ParcelTypes: EParcelType.Email,
            parcelTypeSelected: IParcelTypes.Email,
            Icon: <EmailIcon></EmailIcon>,
          });
        }
      });

      console.log("List", list);
      const newList = [...selected, ...list];
      setSelected(newList);
      // setValue(`${cas.Client ? `${cas.Client} |` : ''} ${cas.Title}`);
      setActive(false);

      onChoose?.(newList);

      if (list.length > 3) {
        setLimitTags(true);
      }

      setValue("");
    } else if (value.endsWith(" ") && validateEmail(value)) {
      const email = validateEmail(value);
      handleClick(
        {
          Id: randomString(),
          Address: "",
          BuildingNumber: "",
          City: "",
          Claims: [],
          Description: "",
          EDeliveryAddress: null,
          Email: email.address,
          FirstName: "",
          FlatNumber: "",
          IsGlobal: false,
          KRS: "",
          Label: email.label ?? email.address,
          Type: EEntityType.Email,
          ParcelTypes: EParcelType.Email,
        },
        IParcelTypes.Email,
      );
    } else if (
      value.charAt(value.length - 1) === " " &&
      validateEDeliveryAddress(value.replaceAll(" ", ""))
    ) {
      const ade = value.replaceAll(" ", "");

      const entity: IEntity = {
        Id: randomString(),
        Address: "",
        BuildingNumber: "",
        City: "",
        Claims: [],
        Description: "",
        EDeliveryAddress: ade.toLocaleUpperCase(),
        Email: "",
        FirstName: "",
        FlatNumber: "",
        IsGlobal: false,
        KRS: "",
        Label: ade.toLocaleUpperCase(),
        Type: EEntityType.EDelivery,
        ParcelTypes: EParcelType.EDelivery,
        internalId: randomString(),
        isBusy: true,
      };

      handleClick(entity, IParcelTypes.EDelivery);
      validateEntity(entity);
    }
  };
  const [isSelected, setIsSelected] = useState(false);
  useEffect(() => {
    checkCanAdd();
  }, [value]);

  const hClick = () => {
    // if (e.code === "Backspace" && !value.length && isSelected) {
    //   const newList: IEntity[] = [...selected].slice(0, -1);
    //   setSelected(newList);
    //   onChoose?.(newList);
    // }
  };

  useEffect(() => {
    document.addEventListener("keydown", hClick);
    return () => {
      document.removeEventListener("keydown", hClick);
    };
  }, [value, isSelected]);

  useEffect(() => {
    if (selected && selected.length > 8) {
      setLimitTags(true);
    }
  }, [selected]);

  const [, setSelectedEntity] = useState<IEntity | null>(null);

  const forwardRef = useRef<HTMLInputElement>();

  useEffect(() => {
    let p = 0;

    if (setShowDw) {
      p += 46;
    }

    if (c) {
      p += 100;
    }

    setRightPadding(p);
  }, [setShowDw, c, limitTags, selected]);

  // Dodaje odbiorcę znalezionego w BAE (rządowe e-Doręczenia) jako przesyłkę ExternalEDelivery.
  const addBaeResult = (r: IGovAddressSearchResult) => {
    if (!r.EDeliveryAddress) return;

    const officialId = (reg: string) =>
      r.OfficialIds?.find(
        (o) => o.ReferenceRegistry?.toLowerCase() === reg,
      )?.Id;

    const name =
      r.EntityName ||
      [r.Name, r.Surname].filter(Boolean).join(" ") ||
      r.EDeliveryAddress;

    const entity: IEntity = {
      Id: r.EDeliveryAddress,
      internalId: randomString(),
      Type: EEntityType.ExternalEDelivery,
      EDeliveryAddress: r.EDeliveryAddress,
      ParcelTypes: EParcelType.ExternalEDelivery,
      parcelTypeSelected: IParcelTypes.ExternalEDelivery,
      Label: name,
      Name: name,
      IsPublic: r.IsPublic,
      NIP: officialId("nip"),
      REGON: officialId("regon"),
      KRS: officialId("krs"),
      isValid: true,
      Icon: <AdeIcon></AdeIcon>,
    };

    setSelected((old) => [...old, entity]);
    onChoose?.([entity]);
  };

  return (
    <div>
      {showBaeSearch && (
        <SearchBaeModal
          toggle={() => setShowBaeSearch(false)}
          onSelect={addBaeResult}
        />
      )}
      <InputWithChips
        variant="standard"
        forwardRef={forwardRef}
        disableBorder={true}
        elements={
          <div className="flex items-center justify-center h-full">
            {setShowDw && (
              <div
                onClick={(e) => {
                  setShowDw?.();
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className={`${styles.clk} ${styles.button2}`}
              >
                DW
              </div>
            )}

            <div
              onClick={(e) => {
                setShowBaeSearch(true);
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Wyszukaj adres w BAE (e-Doręczenia rządowe)"
              className={`${styles.clk} ${styles.button2}`}
            >
              BAE
            </div>

            {selected.length > 1 && (
              <div
                title={
                  limitTags
                    ? t("entities.expandAddressList")
                    : t("entities.collapseAddressList")
                }
                className={`${styles.clk} ${styles.button2} ${styles.limitTagsButton}`}
                onClick={() => setLimitTags(!limitTags)}
              >
                {limitTags && <ExpandIcon></ExpandIcon>}
                {!limitTags && <CollapseIcon></CollapseIcon>}
              </div>
            )}
          </div>
        }
        label={placeholder}
        labelPosition="none"
        limitTags={limitTags}
        setLimitTags={setLimitTags}
        focus={focus}
        onFocus={() => {
          setIsSelected(true);
          setActive(true);
        }}
        checkCanAdd={checkCanAdd}
        onBlur={() => {
          setIsSelected(false);

          console.log("Blur");
          if (validateEmail(value.replaceAll(" ", ""))) {
            const email = value.replaceAll(" ", "").toLocaleLowerCase();
            handleClick(
              {
                Id: randomString(),
                Address: "",
                BuildingNumber: "",
                City: "",
                Claims: [],
                Description: "",
                EDeliveryAddress: email,
                Email: email,
                FirstName: "",
                FlatNumber: "",
                IsGlobal: false,
                KRS: "",
                Label: email,
                Type: EEntityType.Email,
                ParcelTypes: EParcelType.Email,
              },
              IParcelTypes.Email,
            );
          } else if (validateEDeliveryAddress(value.replaceAll(" ", ""))) {
            const ade = value.replaceAll(" ", "").toLocaleLowerCase();

            const entity: IEntity = {
              Id: randomString(),
              Address: "",
              BuildingNumber: "",
              City: "",
              Claims: [],
              Description: "",
              EDeliveryAddress: ade.toLocaleUpperCase(),
              Email: "",
              FirstName: "",
              FlatNumber: "",
              IsGlobal: false,
              KRS: "",
              Label: ade.toLocaleUpperCase(),
              Type: EEntityType.EDelivery,
              ParcelTypes: EParcelType.EDelivery,
              internalId: randomString(),
              isBusy: true,
            };

            handleClick(entity, IParcelTypes.EDelivery);
            validateEntity(entity);
          } else {
            onBlur?.();
          }
        }}
        value={value}
        onChange={(text) => setValue(text)}
        chips={selected.map((member) => ({
          id: member.Id,
          label: member.Label ?? member.Email,
          isValid: member.isValid ?? true,
          isBusy: member.isBusy ?? false,
          Icon: member.Icon,
        }))}
        onChipDelete={handleMemberRemove}
        onLimitedTagsClick={() =>
          setShowLimitedReceiversList(!showLimitedReceiversList)
        }
        rightPadding={rightPadding}
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        isOptionEqualToValue={(option, value) => option.Entity === value.Entity}
        getOptionLabel={(option) =>
          option.Label ?? option.Email ?? t("common.none")
        }
        getOptionAdditional={(option) => option.Additional}
        getOptionIcon={(option) => {
          switch (option.ParcelType) {
            case IParcelTypes.EDelivery:
            case IParcelTypes.EncryptedEDelivery:
              return <AdeIcon></AdeIcon>;
            case IParcelTypes.Email:
              return <EmailIcon></EmailIcon>;
            case IParcelTypes.NotaEncrypted:
              return <NotaIcon></NotaIcon>;
            case IParcelTypes.CourtPortalWriting:
              return <CourtIcon></CourtIcon>;
            default:
              return <LetterIcon></LetterIcon>;
          }
        }}
        renderOption={(option) => {
          if (option.IsSearchAdeButton) {
            if (!option.NotFound && !option.IsBusy) {
              return (
                <div
                  onClick={(e) => {
                    searchAdeForEntity(option);
                    e.stopPropagation();
                  }}
                  className={styles.optionButton}
                >
                  {t("entities.searchAddress")}
                </div>
              );
            }
            if (option.IsBusy) {
              return (
                <div className={styles.adeSearching}>
                  {t("entities.searching")} <Spinner></Spinner>
                </div>
              );
            }
            if (option.NotFound) {
              return (
                <div className={styles.adeNotFound}>
                  {t("entities.adeNotFound")}
                </div>
              );
            }
          }

          return null;
        }}
        options={entitiesOptions}
        loading={isLoadingAddressBook}
        groupBy={(option) =>
          option.CustomGroup ??
          `${
            option.Entity.Label ??
            option.Entity.Email ??
            option.Entity.EDeliveryAddress
          }_ID${option.Entity.Id}`
        }
        handleOnChange={handleOnChange}
      />

      <>
        {false &&
          active &&
          ((!!value.length && searchList.length > 0) ||
            (showWorkspaceMembers && searchedMembers.length > 0)) && (
            <div className={`${styles.list} ${styles.gap}`}>
              <ListControl
                items={searchList}
                selectedItems={selectedItems}
                setSelectedItems={setSearchList}
                getItemId={(i) => i.Id}
                renderItem={(i) => {
                  const s = i as IEntity;

                  const types = getTypeOfParcel(s.ParcelTypes ?? 0);
                  return (
                    <div className={styles.listBox}>
                      <div title={s.Label} className={styles.listTitle}>
                        {s.Label}
                      </div>
                      {types.length ? (
                        types.map((t) => {
                          return (
                            <div
                              onClick={(e) => {
                                if (e.ctrlKey || e.metaKey) return false;

                                handleClick(s, t);
                                console.log("tutaj2 ");
                                // console.log(forwardRef.current);
                                // forwardRef.current.value = "";
                                setValue("");
                              }}
                              className={styles.type}
                            >
                              {t !== IParcelTypes.Email
                                ? translateToPolish(t)
                                : s.Email}
                            </div>
                          );
                        })
                      ) : (
                        <div
                          style={{ color: "#79767A" }}
                          className={styles.type}
                        >
                          {t("entities.noCommChannels")}
                        </div>
                      )}
                    </div>
                  );
                }}
              ></ListControl>
            </div>
          )}
      </>

      {showLimitedReceiversList && (
        <div className={styles.modal} ref={limitedReceiversListRef}>
          {selected.map((member) => (
            <div className={styles.box2}>
              <div className={styles.info}>
                <div className={styles.name}>{member.Label} </div>
                <div className={styles.address}>
                  {member.Icon}{" "}
                  {getEntityAddressForParcelType(
                    member,
                    member.parcelTypeSelected,
                  )}
                  <div
                    className={styles.copy}
                    onClick={() =>
                      navigator.clipboard.writeText(
                        member.Email ?? member.Label,
                      )
                    }
                  >
                    <Tooltip title={t("common.copy")}>
                      <div>
                        <CopyIcon></CopyIcon>
                      </div>
                    </Tooltip>
                  </div>
                </div>
                {(member.parcelTypeSelected === IParcelTypes.EDelivery ||
                  member.parcelTypeSelected ===
                    IParcelTypes.EncryptedEDelivery) && (
                  <div className={styles.adeInfo}>
                    <div className={styles.r1}>
                      {t("entities.recipient")} |{" "}
                      {member.IsPublic
                        ? t("entities.publicEntity")
                        : t("entities.privateEntity")}
                    </div>
                    <div className={styles.r1}>
                      {t("entities.deliveryMode")} |{" "}
                      {member.IsPublic
                        ? t("entities.deliveryOnArrival")
                        : t("entities.deliveryWithin14Days")}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.menu}>
                <Tooltip title={t("common.delete")}>
                  <div>
                    <RemoveIcon
                      className={styles.ic}
                      onClick={() => handleRemove(member)}
                    />
                  </div>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewSearchEntitiesWithCase;
